import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi, useApiQuery } from "@/api/useApi";
import type { PayrollExport, PayrollExportSettings } from "@/api/types";
import { CheckDatePicker } from "@/components/CheckDatePicker";
import { Icon } from "@/components/Icon";
import { longDate, money, todayIso } from "@/format";

function monthStart(today: string): string {
  return `${today.slice(0, 8)}01`;
}

/**
 * Each person's expense reimbursement for a check date (the reimbursement date payroll enters
 * when marking reports paid) or a range of them, downloaded as a CSV for the payroll system. The
 * company ID and pay component are on every row; payroll can change them for one file or save
 * them as the default.
 */
export function PayrollExportPage() {
  const api = useApi();
  const today = todayIso();
  const [mode, setMode] = useState<"date" | "range">("date");
  const [chosenDate, setCheckDate] = useState<string>();
  const [from, setFrom] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const [companyEdit, setCompanyEdit] = useState<string>();
  const [componentEdit, setComponentEdit] = useState<string>();
  const [deductionEdit, setDeductionEdit] = useState<string>();
  const [saved, setSaved] = useState<PayrollExportSettings>();
  const [message, setMessage] = useState<{ ok: boolean; text: string }>();
  const [busy, setBusy] = useState<"save" | "download">();

  // Opens on the most recent check date, until one is chosen.
  const { data: checkDates } = useApiQuery<string[]>("/api/expenses/reports/check-dates");
  const marked = new Set(checkDates ?? []);
  const checkDate = chosenDate ?? checkDates?.[0] ?? today;
  const start = mode === "date" ? checkDate : from;
  const end = mode === "date" ? checkDate : to;
  const valid = start !== "" && end !== "" && start <= end;
  const { data, error, loading } = useApiQuery<PayrollExport>(
    valid ? `/api/expenses/reports/payroll-export?from=${start}&to=${end}` : null,
  );

  const savedCompany = saved?.companyId ?? data?.companyId ?? "";
  const savedComponent = saved?.payComponent ?? data?.payComponent ?? "";
  const companyId = companyEdit ?? savedCompany;
  const payComponent = componentEdit ?? savedComponent;
  const savedDeduction = saved?.deductionPayComponent ?? data?.deductionPayComponent ?? "";
  const deductionComponent = deductionEdit ?? savedDeduction;
  const changed =
    companyId.trim() !== savedCompany ||
    payComponent.trim() !== savedComponent ||
    deductionComponent.trim() !== savedDeduction;
  const rows = data?.rows ?? [];
  // Someone who owes is a deduction row, which needs its own pay component.
  const owes = rows.some((row) => row.amount < 0);
  const missing = rows.filter((row) => !row.workerId);

  function save() {
    setBusy("save");
    setMessage(undefined);
    api
      .put<PayrollExportSettings>("/api/expenses/payroll/export-settings", {
        companyId: companyId.trim(),
        payComponent: payComponent.trim(),
        deductionPayComponent: deductionComponent.trim(),
      })
      .then((settings) => {
        setSaved(settings);
        setCompanyEdit(undefined);
        setComponentEdit(undefined);
        setDeductionEdit(undefined);
        setMessage({ ok: true, text: "Saved as the default." });
      })
      .catch((caught: unknown) =>
        setMessage({ ok: false, text: caught instanceof Error ? caught.message : String(caught) }),
      )
      .finally(() => setBusy(undefined));
  }

  function download() {
    setBusy("download");
    setMessage(undefined);
    const query = new URLSearchParams({
      from: start,
      to: end,
      companyId,
      payComponent,
      deductionPayComponent: deductionComponent,
    });
    api
      .blob(`/api/expenses/reports/payroll-export.csv?${query.toString()}`)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download =
          start === end
            ? `Expense Reimbursements ${start}.csv`
            : `Expense Reimbursements ${start} to ${end}.csv`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
      })
      .catch(() => setMessage({ ok: false, text: "The CSV couldn't be made. Try again." }))
      .finally(() => setBusy(undefined));
  }

  return (
    <div>
      <Link className="back" to="/payroll">
        <Icon name="chevronLeft" size={18} /> Payroll
      </Link>
      <div className="page-head">
        <h1>Payroll CSV</h1>
        <button
          type="button"
          className="btn small"
          onClick={download}
          disabled={
            busy !== undefined ||
            rows.length === 0 ||
            !companyId.trim() ||
            !payComponent.trim() ||
            (owes && !deductionComponent.trim())
          }
        >
          <Icon name="download" size={18} /> {busy === "download" ? "Making…" : "Download CSV"}
        </button>
      </div>
      <p className="muted lead">
        Each person&apos;s expense reimbursement for the payroll system, from reports marked paid
        with this check date (the reimbursement date).
      </p>

      <div className="card payroll-csv-form">
        <div className="segmented" role="radiogroup" aria-label="Dates">
          {(
            [
              ["date", "Check date"],
              ["range", "Date range"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={mode === value}
              className={mode === value ? "on" : ""}
              onClick={() => setMode(value)}
            >
              {label}
            </button>
          ))}
        </div>
        {mode === "date" ? (
          <div className="field">
            <span className="field-label">Check date</span>
            <CheckDatePicker
              label="Check date"
              value={checkDate}
              onChange={setCheckDate}
              marked={marked}
            />
          </div>
        ) : (
          <div className="split">
            <label>
              Check dates from
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
        )}
        {mode === "range" && from > to && (
          <p className="muted small">The end date is before the start date.</p>
        )}
        <div className="split">
          <label>
            Company ID
            <input
              value={companyId}
              onChange={(e) => setCompanyEdit(e.target.value)}
              autoComplete="off"
              maxLength={50}
            />
          </label>
          <label>
            Pay Component
            <input
              value={payComponent}
              onChange={(e) => setComponentEdit(e.target.value)}
              autoComplete="off"
              maxLength={100}
            />
          </label>
        </div>
        <label>
          Deduction Pay Component
          <input
            value={deductionComponent}
            onChange={(e) => setDeductionEdit(e.target.value)}
            autoComplete="off"
            maxLength={100}
            placeholder="For anyone who owes, deducted from their paycheck"
          />
        </label>
        {owes && !deductionComponent.trim() && (
          <p className="muted small">
            Someone owes money on these reports. Enter the deduction pay component to download.
          </p>
        )}
        {changed && (
          <div className="row wrap">
            <button
              type="button"
              className="btn secondary small"
              onClick={save}
              disabled={busy !== undefined || !companyId.trim() || !payComponent.trim()}
            >
              {busy === "save" ? "Saving…" : "Save as default"}
            </button>
            <span className="muted small">Or download with these for this file only.</span>
          </div>
        )}
      </div>

      {message && (
        <div className={message.ok ? "success" : "error"} role="status">
          {message.text}
        </div>
      )}
      {error && (
        <div className="error" role="alert">
          {error.message}
        </div>
      )}
      {loading && !data && <div className="card skeleton line wide" />}

      {data && rows.length === 0 && (
        <div className="card empty">
          <h2>Nothing reimbursed</h2>
          <p className="muted">
            {start === end
              ? `No reports were marked paid with a check date of ${longDate(start)}.`
              : "No reports were marked paid with a check date in this range."}
          </p>
        </div>
      )}

      {missing.length > 0 && (
        <div className="note warn">
          <strong>No worker ID for {missing.map((row) => row.name).join(", ")}</strong>
          <p>
            They&apos;re in the file with the Worker ID blank. Add their employee number in Entra,
            or in the office directory under Settings → Department codes.
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="card gl-table-wrap">
          <table className="gl-table">
            <thead>
              <tr>
                <th>Company ID</th>
                <th>Worker ID</th>
                <th>Pay Component</th>
                <th className="num">Amount</th>
                <th>Employee</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.workerId ?? ""}-${row.name}`}>
                  <td>{companyId}</td>
                  <td>{row.workerId ?? ""}</td>
                  <td>{payComponent}</td>
                  <td className="num">{money(row.amount)}</td>
                  <td className="muted">{row.name}</td>
                </tr>
              ))}
              <tr className="gl-grand">
                <td colSpan={3}>
                  Total · {rows.length} {rows.length === 1 ? "person" : "people"}
                </td>
                <td className="num">{money(data?.total ?? 0)}</td>
                <td />
              </tr>
            </tbody>
          </table>
          <p className="muted small payroll-csv-note">
            The Employee column is here for checking; the file has the first four columns.
          </p>
        </div>
      )}
    </div>
  );
}
